import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBookOpen, FaChartBar, FaCheckCircle, FaSchool, FaUserGraduate } from 'react-icons/fa';
import { useApp } from '../../../context/AppContext';
import { enrollmentAPI } from '../../../utils/api';
import { DashboardChartGrid, KpiGrid, PortalHero, ProgressBar } from '../../../components_shared/PortalDashboard';

const initialSummary = {
  assignedSchools: 0,
  collectedSchools: 0,
  pendingSchools: 0,
  totalEnrollment: 0,
  schools: [],
};

export default function TEODashboardPage() {
  const { user, roles } = useApp();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(initialSummary);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    enrollmentAPI.getCollectionSummary()
      .then(res => setSummary({ ...initialSummary, ...(res.data || {}) }))
      .catch(() => setSummary(initialSummary))
      .finally(() => setLoading(false));
  }, []);

  const roleLabel = roles?.includes('ROLE_TO') ? 'TO' : 'TEO';
  const completion = summary.assignedSchools
    ? Math.round((summary.collectedSchools / summary.assignedSchools) * 100)
    : 0;

  const cards = useMemo(() => [
    { icon: <FaSchool />, label: 'Assigned Schools', value: summary.assignedSchools, desc: 'Schools in your area', onClick: () => navigate('/collected-enrollment'), className: 'eduss-dashboard-card-blue' },
    { icon: <FaCheckCircle />, label: 'Collected', value: summary.collectedSchools, desc: 'Schools with saved enrollment', onClick: () => navigate('/collected-enrollment'), className: 'eduss-dashboard-card-green' },
    { icon: <FaBookOpen />, label: 'Pending', value: summary.pendingSchools, desc: 'Schools still waiting', onClick: () => navigate('/collected-enrollment'), className: 'eduss-dashboard-card-yellow' },
    { icon: <FaUserGraduate />, label: 'Students', value: summary.totalEnrollment.toLocaleString(), desc: 'Total enrollment captured', onClick: () => navigate('/collected-enrollment'), className: 'eduss-dashboard-card-green' },
  ], [summary, navigate]);

  const charts = useMemo(() => {
    const schoolRows = (summary.schools || []).slice(0, 6).map(school => ({
      name: school.name || school.school_name || `School ${school.id}`,
      value: Number(school.totalEnrollment || 0),
    }));
    const collectionRows = [
      { name: 'Assigned', value: Number(summary.assignedSchools || 0) },
      { name: 'Collected', value: Number(summary.collectedSchools || 0) },
      { name: 'Pending', value: Number(summary.pendingSchools || 0) },
    ];

    return [
      { title: 'School Enrollment', caption: 'Top schools', type: 'bar', data: schoolRows.length ? schoolRows : [{ name: 'No Data', value: 0 }], color: '#0f6fad' },
      { title: 'Collection Trend', caption: `${completion}% complete`, type: 'line', data: collectionRows, color: '#10ac84' },
    ];
  }, [summary, completion]);

  return (
    <div className="eduss-dashboard">
      <PortalHero title={`${roleLabel} Enrollment Dashboard`} subtitle={`Welcome, ${user?.username || roleLabel}. Track and enter enrollment for your assigned schools.`} />
      <KpiGrid cards={cards} loading={loading} />
      <DashboardChartGrid charts={charts} />

      <div className="eduss-dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 18 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>Enrollment Progress</h2>
            <p style={{ margin: 0, color: '#6c757d' }}>{completion}% of assigned schools have enrollment saved.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="eduss-home-cta" type="button" onClick={() => navigate('/collected-enrollment')}>
              <FaChartBar /> Open Collection
            </button>
            <button className="eduss-home-cta" type="button" onClick={() => navigate('/teo/requisitions')}>
              <FaBookOpen /> Warehouse Request
            </button>
            <button className="eduss-home-cta" type="button" onClick={() => navigate('/teo/handovers')}>
              <FaSchool /> Handover
            </button>
          </div>
        </div>

        <ProgressBar value={completion} />

        <div style={{ overflowX: 'auto' }}>
          <table className="mini-info-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>School</th>
                <th>Total Enrollment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(summary.schools || []).slice(0, 8).map(school => (
                <tr key={school.id}>
                  <td>{school.name}</td>
                  <td>{Number(school.totalEnrollment || 0).toLocaleString()}</td>
                  <td>{school.totalEnrollment > 0 ? 'Collected' : 'Pending'}</td>
                </tr>
              ))}
              {!loading && !summary.schools?.length && (
                <tr><td colSpan="3">No assigned schools found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
